import { useEffect, useRef, useState } from "react";
import { Languages, Loader2, MapPin, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Carte « Traduction anglaise (Gemini) » — le traducteur est le Gemini du
// propriétaire depuis le 2026-10-20 (demande : « mon Gemini partout ») :
// deux tâches de fond — fiches PRODUITS (nom + accroche + description, alimente
// le site /en et le flux Shopping anglais) et pages VILLES (SEO local : titre,
// description, méta, région, mots-clés — comble uniquement les champs vides).
// Progression en direct, interruption possible, courriel récapitulatif à
// info@trophee.ca à la fin de chaque tournée.

function BlocTraduction({ base, testid, icone: Icone, titre, description, libelles }) {
  const [d, setD] = useState(null);   // {restantes, job:{running,total,done,erreurs}}
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const rafraichir = async () => {
    try {
      const { data } = await api.get(`${base}/statut`);
      setD(data);
      return data;
    } catch { return null; }
  };

  // Suivi en direct tant que la tâche tourne
  useEffect(() => {
    rafraichir();
    pollRef.current = setInterval(() => { rafraichir(); }, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const demarrer = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/start`);
      toast.success(`Traduction lancée — Gemini traduit ${libelles} en tâche de fond`);
      await rafraichir();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lancement impossible");
    } finally { setBusy(false); }
  };

  const arreter = async () => {
    try {
      await api.post(`${base}/stop`);
      toast.success("Arrêt demandé — la traduction s'interrompt après le lot en cours");
      await rafraichir();
    } catch { toast.error("Arrêt impossible"); }
  };

  const job = d?.job || {};
  const running = !!job.running;
  const pct = running && job.total ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;

  return (
    <div className="border-t border-ivory/10 first:border-t-0 pt-5 first:pt-0 mt-5 first:mt-0">
      <p className="font-semibold flex items-center gap-2 text-sm">
        <Icone className="w-4 h-4 text-gold" /> {titre}
      </p>
      <p className="text-xs text-ivory/70 mt-1 mb-4">{description}</p>
      {!d ? (
        <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Lecture…</p>
      ) : (
        <>
          <p className="text-sm mb-4" data-testid={`${testid}-restantes`}>
            <strong className="text-gold text-lg">{d.restantes.toLocaleString("fr-CA")}</strong>
            <span className="text-ivory/70"> {d.restantes > 1 ? "restent à traduire" : "reste à traduire"}</span>
          </p>
          {running && (
            <div className="mb-4" data-testid={`${testid}-progress`}>
              <div className="flex justify-between text-xs text-ivory/60 mb-1.5">
                <span>Traduction en cours… {job.done || 0} / {job.total || 0}</span>
                <span>{pct} %{job.erreurs ? ` · ${job.erreurs} en erreur` : ""}</span>
              </div>
              <div className="h-2 bg-ink rounded-full overflow-hidden border border-ivory/10">
                <div className="h-full bg-gold transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}
          {!running && job.total > 0 && (
            <p className="text-xs text-emerald-300 mb-4" data-testid={`${testid}-derniere`}>
              Dernière tournée : {job.done || 0} traduite{(job.done || 0) > 1 ? "s" : ""}
              {job.erreurs ? ` · ${job.erreurs} en erreur (reprises à la prochaine tournée)` : ""}
              {" · "}récapitulatif envoyé à info@trophee.ca
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <button onClick={demarrer} disabled={busy || running || d.restantes === 0}
              data-testid={`${testid}-start`}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
              {busy || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icone className="w-4 h-4" />}
              {running ? "Traduction en cours…" : d.restantes === 0 ? "Tout est traduit" : `Traduire ${d.restantes.toLocaleString("fr-CA")}`}
            </button>
            {running && (
              <button onClick={arreter} data-testid={`${testid}-stop`}
                className="inline-flex items-center gap-2 border border-red-400/50 text-red-300 px-6 py-3 rounded-full text-sm hover:bg-red-500/15 transition-colors duration-300">
                <Square className="w-3.5 h-3.5" /> Arrêter
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function TraductionCard() {
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-8" data-testid="traduction-card">
      <p className="font-semibold flex items-center gap-2 mb-1">
        <Languages className="w-4 h-4 text-gold" /> Traduction anglaise (Gemini)
      </p>
      <p className="text-xs text-ivory/70 mb-5">
        Chaque traduction utilise votre clé IA (crédits). Un courriel récapitulatif
        part à info@trophee.ca quand une tournée se termine.
      </p>
      <BlocTraduction
        base="/admin/claude/traduction"
        testid="traduction"
        icone={Languages}
        titre="Fiches produits"
        description="Traduit le nom, l'accroche et la description des fiches sans anglais — alimente le site /en et le flux Shopping anglais."
        libelles="les fiches produits"
      />
      <BlocTraduction
        base="/admin/claude/traduction-villes"
        testid="traduction-villes"
        icone={MapPin}
        titre="Pages villes (SEO local)"
        description="Comble les champs anglais vides des pages villes (titre, description, méta, région, mots-clés) — les traductions existantes ne sont jamais écrasées."
        libelles="les pages villes"
      />
    </div>
  );
}
